import { useEffect, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Typography,
  Stack,
  Link,
  styled,
} from "@mui/material";

import EditIcon from "@mui/icons-material/Edit";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import RemoveModeratorIcon from "@mui/icons-material/RemoveModerator";
import BusinessIcon from "@mui/icons-material/Business";
import PlaceIcon from "@mui/icons-material/Place";
import DeliveryDiningIcon from "@mui/icons-material/DeliveryDining";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import SimCardIcon from "@mui/icons-material/SimCard";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import FlagSharpIcon from "@mui/icons-material/FlagSharp";
import StarIcon from "@mui/icons-material/Star";

import { useOrganization } from ".";
import { useAuth } from "../../../contexts/AuthContext";
import { fetchData } from "../../../fetchData";
import { formatDate } from "../../../utils";
import { formatOrganizationDocument } from "../../../utils/validateDocument";

import Edit from "./edit";

const SectionTitle = ({ children }: { children: ReactNode }) => (
  <Typography
    variant="caption"
    color="text.secondary"
    fontWeight="bold"
    display="block"
    textAlign="center"
    sx={{ lineHeight: 1.3, mb: 1 }}
  >
    {children}
  </Typography>
);

type InfoColumn = {
  key: string;
  title: string;
  value: ReactNode;
};

const infoGridSx = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  justifyItems: "center",
  alignItems: "center",
  columnGap: 2,
  rowGap: 0.5,
  width: "100%",
  mt: 2,
  mb: 2,
} as const;

const infoValueCellSx = {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  width: "100%",
  px: 0.5,
  textAlign: "center",
  "& .MuiTypography-root": { wordBreak: "break-word" },
} as const;

const InfoGridRows = ({
  columns,
  titleRow,
  valueRow,
}: {
  columns: InfoColumn[];
  titleRow: number;
  valueRow: number;
}) => (
  <>
    {columns.map((col, index) => (
      <Typography
        key={`${col.key}-title`}
        variant="caption"
        color="text.secondary"
        fontWeight="bold"
        textAlign="center"
        sx={{
          gridRow: titleRow,
          gridColumn: index + 1,
          lineHeight: 1.3,
          width: "100%",
        }}
      >
        {col.title}
      </Typography>
    ))}
    {columns.map((col, index) => (
      <Box
        key={`${col.key}-value`}
        sx={{
          ...infoValueCellSx,
          gridRow: valueRow,
          gridColumn: index + 1,
        }}
      >
        {col.value}
      </Box>
    ))}
  </>
);

const ProfileInfoLayout = ({
  topColumns,
  profileColumns,
}: {
  topColumns: InfoColumn[];
  profileColumns: InfoColumn[];
}) => (
  <Box sx={infoGridSx}>
    <InfoGridRows columns={topColumns} titleRow={1} valueRow={2} />
    <Divider
      sx={{ gridColumn: "1 / -1", gridRow: 3, width: "100%", my: 1.5 }}
    />
    <InfoGridRows columns={profileColumns} titleRow={4} valueRow={5} />
  </Box>
);

const GoldenChip = styled(Chip)(() => ({
  background: "linear-gradient(45deg, #F4D03F, #DAA520)",
  color: "#fff",
  fontWeight: "bold",
  boxShadow: "0 2px 8px rgba(218, 165, 32, 0.4)",
  position: "relative",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    top: "-50%",
    left: "-50%",
    width: "200%",
    height: "200%",
    background:
      "linear-gradient(45deg, transparent, rgba(255,255,255,0.3), transparent)",
    transform: "rotate(45deg)",
    animation: "shine 3s cubic-bezier(0.4, 0, 0.2, 1) infinite",
  },
  "@keyframes shine": {
    "0%": {
      transform: "translateX(-100%) rotate(45deg)",
    },
    "100%": {
      transform: "translateX(100%) rotate(45deg)",
    },
  },
  "& .MuiChip-icon": {
    color: "#B8860B",
  },
}));

const Profile = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const organization = useOrganization();
  const { data } = organization;
  const [isEditing, setIsEditing] = useState(false);
  const [legal, setLegal] = useState<any>({});

  const legalResponsible = async () => {
    try {
      const person = await fetchData(`/users/${data.legal_responsible}`, {
        method: "GET",
      });
      setLegal(person);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    setIsEditing(false);
    if (!data.legal_responsible) return;
    legalResponsible();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization]);

  const topColumns: InfoColumn[] = [
    {
      key: "reference",
      title: "Código",
      value: (
        <Typography variant="body1" fontWeight="bold">
          {data.reference}
        </Typography>
      ),
    },
    {
      key: "status",
      title: "Status",
      value: (
        <Stack
          direction="row"
          justifyContent="center"
          alignItems="center"
          spacing={0.5}
        >
          {data.status ? (
            <VerifiedUserIcon color="success" fontSize="small" />
          ) : (
            <RemoveModeratorIcon color="error" fontSize="small" />
          )}
          <Typography
            variant="body1"
            color={`${data.status ? "success" : "error"}.main`}
          >
            {data.status ? "Ativo" : "Inativo"}
          </Typography>
        </Stack>
      ),
    },
  ];

  const profileColumns: InfoColumn[] = [
    {
      key: "profile",
      title: "Carteira",
      value: <Typography variant="body1">{data.profile || "—"}</Typography>,
    },
    {
      key: "profileTier",
      title: "Relacionamento",
      value: <Typography variant="body1">{data.profileTier || "—"}</Typography>,
    },
  ];

  return (
    <Card>
      <CardContent>
        {!isEditing && (
          <div style={{ textAlign: "center" }}>
            <ProfileInfoLayout
              topColumns={topColumns}
              profileColumns={profileColumns}
            />

            <SectionTitle>Produtos</SectionTitle>

            <Stack
              direction="row"
              justifyContent="center"
              alignItems="center"
              spacing={1}
              sx={{
                mb: 3,
                flexWrap: "wrap",
                gap: 1,
              }}
            >
              {data.products?.saveRH?.active && (
                <Chip
                  label="SAVE RH"
                  color="primary"
                  variant="filled"
                  size="small"
                />
              )}
              {data.products?.saveFinanc?.active && (
                <Chip
                  label="SAVE Financeiro"
                  color="primary"
                  variant="filled"
                  size="small"
                />
              )}
              {data.products?.nfsEmit?.active && (
                <Chip
                  label="Emissão NFS"
                  color="primary"
                  variant="filled"
                  size="small"
                />
              )}
              {data.products?.auditoria?.active && (
                <Chip
                  label="Auditoria"
                  color="primary"
                  variant="filled"
                  size="small"
                />
              )}
              {data.products?.pjSimples?.active && (
                <Chip
                  label="PJ Simples"
                  color="primary"
                  variant="filled"
                  size="small"
                />
              )}
              {data.products?.essencial?.active && (
                <Chip
                  label="Essencial"
                  color="primary"
                  variant="filled"
                  size="small"
                />
              )}
              {data.products?.det?.active && (
                <Chip
                  label="DET"
                  color="primary"
                  variant="filled"
                  size="small"
                />
              )}
              {data.products?.persona?.active && (
                <Chip
                  label="Persona"
                  color="primary"
                  variant="filled"
                  size="small"
                />
              )}
              {data.products?.prestacaoContas?.active && (
                <Chip
                  label="Prestação de Contas"
                  color="primary"
                  variant="filled"
                  size="small"
                />
              )}
              {data.products?.taxAdvisory?.active && (
                <Chip
                  label="Assessoria Tributária"
                  color="primary"
                  variant="filled"
                  size="small"
                />
              )}
              {data.products?.saveRH?.active &&
                data.products?.saveFinanc?.active &&
                data.products?.essencial?.active &&
                data.products?.det?.active && (
                  <GoldenChip
                    label="Integra ATLC"
                    icon={<StarIcon />}
                    size="small"
                    sx={{ ml: 2 }}
                    color="default"
                  />
                )}
            </Stack>

            <Divider sx={{ my: 3 }} />

            {data.critical && (
              <div>
                <FlagSharpIcon color="error" />
                <Typography
                  variant="caption"
                  component="div"
                  fontWeight="bold"
                  sx={{ mb: 3 }}
                >
                  Situação crítica
                </Typography>
              </div>
            )}

            {data.document && (
              <div>
                <BusinessIcon color="primary" />
                <Typography variant="body1">
                  {formatOrganizationDocument(
                    data.document,
                    data.personType ?? "PJ",
                  )}
                </Typography>
              </div>
            )}

            {data.legal_responsible && legal.name && (
              <div>
                <AdminPanelSettingsIcon color="primary" sx={{ mt: 3 }} />
                <Link
                  onClick={() => {
                    navigate(`/persons/${legal._id}`);
                  }}
                  sx={{
                    cursor: "pointer",
                  }}
                >
                  <Typography variant="body1">{legal.name}</Typography>
                </Link>
              </div>
            )}

            {data.digital_certificate?.expiricy && (
              <>
                <SimCardIcon color="primary" sx={{ mt: 3 }} />
                <Typography variant="caption" component="div" fontWeight="bold">
                  A1 válido até
                </Typography>
                <Typography variant="body1">
                  {formatDate(data.digital_certificate?.expiricy)}
                </Typography>
              </>
            )}

            {data.init_date && (
              <>
                <CalendarMonthIcon color="primary" sx={{ mt: 3 }} />
                <Typography variant="caption" component="div" fontWeight="bold">
                  Início de Contrato
                </Typography>
                <Typography variant="body1">
                  {formatDate(data.init_date, "D/M/Y")}
                </Typography>
              </>
            )}

            {data.address && data.address.state && data.address.city && (
              <div>
                <PlaceIcon color="primary" sx={{ mt: 3 }} />
                <Typography variant="body1">
                  {data.address.street}
                  {data.address.number && `, ${data.address.number}`}
                  {(data.address.street || data.address.number) && <br />}
                  {data.address.complement && data.address.complement}
                  {data.address.complement && <br />}
                  {data.address.district && `${data.address.district} - `}
                  {`${data.address.city} - ${data.address.state}`}
                  {data.address.zipcode && <br />}
                  {data.address.zipcode && `CEP: ${data.address.zipcode}`}
                </Typography>
              </div>
            )}

            {data.delivery_address && data.delivery_address.zipcode && (
              <div>
                <DeliveryDiningIcon color="primary" sx={{ mt: 3 }} />
                <Typography variant="body1">
                  {data.delivery_address.street}, {data.delivery_address.number}
                  <br />
                  {data.delivery_address.complement &&
                    data.delivery_address.complement}
                  {data.delivery_address.complement && <br />}
                  {`${data.delivery_address.district} - ${data.delivery_address.city} - ${data.delivery_address.state}`}
                  {data.delivery_address.zipcode && <br />}
                  CEP: {data.delivery_address.zipcode}
                </Typography>
              </div>
            )}

            {auth?.user.permission_level === "manager" && (
              <>
                <Divider sx={{ my: 3 }} />
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    setIsEditing(true);
                  }}
                >
                  <EditIcon fontSize="small" sx={{ mr: 1 }} /> Editar
                </Button>
              </>
            )}
          </div>
        )}

        {isEditing && <Edit setIsEditing={setIsEditing} />}
      </CardContent>
    </Card>
  );
};

export default Profile;
